'use client';

import { ChangeEvent, useCallback, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { BaseInput, FieldWrapper } from '@/components/ui/input';
import { useCombobox } from '@/hooks/useCombobox';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import type { MenuListOption } from './MenuList';
import { MenuList } from './MenuList';
import { normalizeOptions, type SelectOptionInput } from './options';
import { MdArrowDropDown } from 'react-icons/md';

/**
 * Accessible select component built from:
 * - `FieldWrapper` → label, required marker, helper/error text.
 * - `BaseInput`    → text input shell acting as the trigger.
 * - `MenuList`     → portal-based dropdown / bottom sheet with options.
 *
 * Features:
 * - Controlled (`value` + `onChange`) and uncontrolled (`defaultValue`) usage.
 * - Optional desktop filtering via `filterable` (typing in the input filters options).
 * - Mobile-friendly bottom sheet on small screens.
 */
export interface SelectProps {
  /** Plain strings or full option objects — strings become `{value: s, label: s}`. */
  options: readonly SelectOptionInput[];
  value?: string;
  defaultValue?: string;
  onChange?: (value: string, inputKey?: string) => void;
  placeholder?: string;
  label?: string;
  required?: boolean;
  /** Message shown when required and value is empty. Default from BaseInput: "This field is required". */
  requiredMessage?: string;
  error?: string | boolean;
  helperText?: string;
  disabled?: boolean;
  /** Title shown in bottom sheet header on mobile (e.g. label or "Select option"). */
  menuTitle?: string;
  /** Allow user to clear the current selection via an X icon. */
  clearable?: boolean;
  clearAriaLabel?: string;
  /**
   * Allow typing to filter options: in the trigger on desktop, and in a search box
   * inside the bottom sheet on mobile.
   */
  filterable?: boolean;
  /** Message shown when no options match (e.g. after filtering). Default: "No data found" */
  noOptionsText?: string;
  /** Placeholder for the mobile search box (filterable mode). Default: "Search". */
  searchPlaceholder?: string;
  className?: string;
  inputClassName?: string;
  containerClassName?: string;
  fieldClassName?: string;
  /** When true, removes the bottom padding reserved for helper/error text. */
  hideBottomPadding?: boolean;
  id?: string;
  inputKey?: string;
  dataCy?: string;
  /** Content rendered above the options list (e.g. "Add new …" link). */
  headerSlot?: ReactNode;
  /** Override dropdown z-index when the Select is inside a high-stacking surface. */
  menuZIndex?: number;
  /**
   * Optional custom renderer for each dropdown option.
   * When provided, replaces the default label text inside each option button.
   * The button wrapper (accessibility, focus, selection) is still managed internally.
   */
  renderOption?: (option: MenuListOption, isSelected: boolean) => ReactNode;
  /**
   * Optional custom renderer for the selected value displayed in the input trigger.
   * When provided, replaces the plain label text with custom ReactNode content.
   * Receives the selected option data; returns a ReactNode.
   * Only rendered when a value is selected and the dropdown is not in typing mode.
   */
  renderValue?: (option: MenuListOption) => ReactNode;
}

/**
 * Map the current `value` to its corresponding option label.
 * Returns an empty string when the value is empty or no matching option exists.
 */
function getLabelFromValue(options: MenuListOption[], value: string | undefined): string {
  if (value == null || value === '') return '';
  const opt = options.find((o) => o.value === value);
  return opt?.label ?? '';
}

export function Select({
  options,
  value: controlledValue,
  defaultValue,
  onChange,
  placeholder = 'Select...',
  label,
  required,
  requiredMessage,
  error,
  helperText,
  disabled,
  menuTitle,
  clearable = false,
  clearAriaLabel,
  filterable = true,
  noOptionsText,
  searchPlaceholder,
  className = '',
  inputClassName = '',
  containerClassName = '',
  fieldClassName = '',
  hideBottomPadding = false,
  id,
  inputKey,
  dataCy,
  headerSlot,
  menuZIndex,
  renderOption,
  renderValue,
}: SelectProps) {
  // Stable IDs used to wire label, helper text, and listbox together.
  const generatedId = useId();
  const inputId = id ?? `select-input-${generatedId}`;
  const helperId = `${inputId}-helper`;
  const listboxId = `select-listbox-${id ?? generatedId}`;

  const normalizedOptions = useMemo(() => normalizeOptions(options), [options]);

  // Uncontrolled selection state; ignored when `value` prop is provided.
  const [uncontrolledValue, setUncontrolledValue] = useState(defaultValue ?? '');

  // Filter text (desktop filterable mode only); cleared when menu closes.
  const [searchText, setSearchText] = useState('');

  // Validation error forwarded from `BaseInput` (required + validators).
  const [validationError, setValidationError] = useState<string | null>(null);

  // Anchor element the dropdown is positioned against (desktop).
  const anchorRef = useRef<HTMLDivElement>(null);
  // Container element for the BaseInput; useful for future positioning/scroll logic.
  const baseInputContainerRef = useRef<HTMLDivElement | null>(null);
  // When true, the next handleOpen call is ignored (used after clear to avoid reopening the menu on focus).
  const skipNextOpenRef = useRef(false);

  const handleValidationChange = useCallback((error: string | null) => {
    setValidationError(error);
  }, []);

  const isMobile = useMediaQuery('(max-width: 767px)');

  const {
    open,
    setOpen,
    handleOpen: hookHandleOpen,
    handleClose: hookHandleClose,
    handleTriggerKeyDown,
    filteredOptions: menuOptions,
  } = useCombobox<MenuListOption>({
    options: normalizedOptions,
    filterText: searchText,
    getOptionLabel: (opt) => opt.label,
    filterOptions: filterable ? 'client' : 'none',
    disabled,
    listboxId,
  });

  // When `value` prop is provided we treat the component as controlled.
  const isControlled = controlledValue !== undefined;
  const value = isControlled ? controlledValue : uncontrolledValue;

  // Human-readable label derived from the selected option.
  const selectedLabel = getLabelFromValue(normalizedOptions, value);

  // Hide error while menu is open — interaction is in progress.
  const displayError = open
    ? null
    : typeof error === 'string'
      ? error
      : error === true
        ? ''
        : validationError;

  // In desktop filterable mode, keep the input editable while the menu is open.
  const isTyping = filterable && !isMobile && open;
  const displayValue = isTyping ? searchText : selectedLabel;

  /**
   * Open the menu unless the field is disabled.
   * Skips opening when called right after clear (focus moves to input and would reopen the menu).
   */
  const handleOpen = useCallback(() => {
    if (skipNextOpenRef.current) {
      skipNextOpenRef.current = false;
      return;
    }
    hookHandleOpen();
  }, [hookHandleOpen]);

  /**
   * Close the menu and clear any transient search text.
   */
  const handleClose = useCallback(() => {
    hookHandleClose();
    setSearchText('');
  }, [hookHandleClose]);

  /**
   * When an option is chosen from the menu:
   * - update uncontrolled state (if applicable),
   * - notify parent via `onChange`,
   * - clear search text and close the menu.
   */
  const handleSelect = useCallback(
    (selected: string) => {
      if (!isControlled) setUncontrolledValue(selected);
      onChange?.(selected, inputKey);
      setSearchText('');
      setOpen(false);
    },
    [isControlled, onChange, setOpen]
  );

  /**
   * Change handler used only for desktop filterable mode.
   * In all other cases the input remains effectively read-only.
   */
  const handleInputChange = useCallback(
    (newValue: string, _inputKey?: string, _event?: ChangeEvent<HTMLInputElement>) => {
      if (!(filterable && !isMobile && open)) return;
      setSearchText(newValue);
    },
    [filterable, isMobile, open]
  );

  /**
   * Clear the current selection:
   * - reset uncontrolled value (if applicable),
   * - call `onChange` with an empty string,
   * - clear search text and close the menu.
   * Sets skipNextOpenRef so the subsequent focus on the input does not reopen the menu.
   */
  const handleClear = useCallback(() => {
    skipNextOpenRef.current = true;
    if (!isControlled) {
      setUncontrolledValue('');
    }
    onChange?.('');
    setSearchText('');
    setOpen(false);
  }, [isControlled, onChange]);

  const hasSelection = Boolean(value);

  // Title used for the mobile bottom sheet header and list labelling.
  const displayTitle = menuTitle ?? label ?? 'Select option';

  // Custom overlay for the input when renderValue is provided and a value is selected.
  // Only hide the overlay when the user is actively typing a filter (searchText is non-empty),
  // not just when the dropdown opens. This prevents a layout shift (absolute → flow) that
  // causes focus/blur flicker when the dropdown opens.
  const selectedOption = value ? normalizedOptions.find((o) => o.value === value) : undefined;
  const isActivelyFiltering = isTyping && searchText !== '';
  const inputOverlay =
    renderValue && selectedOption && !isActivelyFiltering ? renderValue(selectedOption) : undefined;

  return (
    <div
      ref={anchorRef}
      className={`tw:relative tw:min-w-0 tw:w-full ${className}`.trim()}
      onClick={disabled ? undefined : handleOpen}
      data-cy={dataCy}
    >
      <FieldWrapper
        label={label}
        required={required}
        error={displayError}
        helperText={helperText}
        hideBottomPadding={hideBottomPadding}
        id={inputId}
        helperId={helperId}
        className={fieldClassName}
      >
        <BaseInput
          id={inputId}
          // Editable only when typing into the trigger is intended (desktop filterable + open).
          // Read-only on mobile also suppresses the browser's native clear "X", which cleared
          // the DOM value but not the controlled selection.
          readOnly={isMobile || !filterable || !open ? true : undefined}
          value={displayValue}
          placeholder={placeholder}
          required={required}
          suppressBlurValidation={open}
          requiredMessage={requiredMessage}
          error={error}
          helperText={helperText}
          disabled={disabled}
          // A disabled Select cannot be opened, so the dropdown indicator would be a
          // misleading affordance (e.g. the auto-populated, read-only Vehicle Body field). PB-2725
          rightIcon={
            disabled ? undefined : (
              <MdArrowDropDown data-cy="select-trigger" className="tw:h-5 tw:w-5" aria-hidden />
            )
          }
          onKeyDown={handleTriggerKeyDown}
          onChange={handleInputChange}
          onClear={clearable && hasSelection && !disabled ? handleClear : undefined}
          clearAriaLabel={
            clearable && hasSelection && !disabled
              ? (clearAriaLabel ?? 'Clear selection')
              : undefined
          }
          onValidationChange={handleValidationChange}
          inputClassName={`tw:cursor-pointer ${inputClassName}`.trim()}
          containerClassName={containerClassName}
          containerRef={baseInputContainerRef}
          overlay={inputOverlay}
          // ARIA combobox pattern: the input must have role="combobox" so that
          // aria-expanded, aria-haspopup, and aria-controls are valid attributes.
          role="combobox"
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={listboxId}
          aria-autocomplete="none"
          autoComplete="new-password"
        />
      </FieldWrapper>
      <MenuList
        open={open}
        onClose={handleClose}
        options={menuOptions}
        onSelect={handleSelect}
        title={displayTitle}
        anchorRef={baseInputContainerRef}
        listboxId={listboxId}
        filterable={filterable}
        noOptionsText={noOptionsText ?? 'No data found'}
        selectedValue={value}
        renderOption={renderOption}
        headerSlot={headerSlot}
        menuZIndex={menuZIndex}
        searchValue={searchText}
        onSearchChange={filterable ? setSearchText : undefined}
        searchPlaceholder={searchPlaceholder}
      />
    </div>
  );
}
